<script setup>
import { computed } from 'vue'
import CountryFlag from '@/components/CountryFlag.vue'
import { useTrackTitle } from '@/composables/useTrackTitle'

/**
 * The name of a track: the leading name and the other one beside it or under it.
 *
 * Every page writes a track through this component (or through TrackName, when the name is a link),
 * so which of the two names leads stays a matter of configuration (see useTrackTitle.js) instead of a
 * conditional in each page. The flag rides on the line that carries the country's name.
 */
const props = defineProps({
  /** Track id, as the server configures it (config/tracks.php) and the API returns it. */
  trackId: { type: String, default: null },
  /** 'inline' keeps the two names on one line, 'stacked' puts the second one under the first. */
  variant: { type: String, default: 'inline' },
  /** Show the country's flag beside its name. */
  flag: { type: Boolean, default: true },
  /**
   * Show both names even where the country leads: the second one is a page title on its own, not a
   * label in a list (see views/TrackView.vue and views/events/EventTrackView.vue).
   */
  bothNames: { type: Boolean, default: false },
})

/** Which of the two names leads comes from the site (see useTrackTitle.js). */
const { primaryIsCountry, primaryName, secondaryName, countryCode } = useTrackTitle()

/** Where the country leads, it names the track on its own: the track name is only a page title's. */
const showSecondary = computed(() => Boolean(secondaryName(props.trackId))
  && (props.bothNames || !primaryIsCountry.value))

const country = computed(() => (props.flag ? countryCode(props.trackId) : null))
const flagOnPrimary = computed(() => primaryIsCountry.value)
</script>

<template>
  <span v-if="trackId" class="track-title" :class="`track-title--${variant}`">
    <CountryFlag v-if="country && flagOnPrimary" :country="country" class="track-title__flag" />
    <span class="track-title__primary">{{ primaryName(trackId) }}</span>
    <span v-if="showSecondary" class="track-title__secondary">
      <CountryFlag v-if="country && !flagOnPrimary" :country="country" class="track-title__flag" />
      {{ secondaryName(trackId) }}
    </span>
  </span>
  <span v-else class="track-title">–</span>
</template>

<style scoped>
.track-title__flag {
  margin-right: 6px;
}

.track-title--inline .track-title__secondary {
  margin-left: 6px;
  font-size: 0.85em;
  opacity: 0.7;
}

.track-title--stacked .track-title__secondary {
  display: block;
  margin-top: 2px;
  font-size: 0.8em;
  font-weight: 400;
  opacity: 0.7;
}
</style>
